import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Key,
  FileText,
  Download,
  Copy,
  RefreshCw,
  AlertTriangle,
  Activity,
  KeyRound
} from 'lucide-react';
import { certificateApi } from '../../services/iso20022Api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatRelativeTime, copyToClipboard, downloadFile } from '../../utils/helpers';
import ExportKeystoreModal from './ExportKeystoreModal';

const CertificateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [copied, setCopied] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    loadCertificate();
  }, [id]);

  const loadCertificate = async () => {
    try {
      setLoading(true);
      const [certData, auditData] = await Promise.all([
        certificateApi.getCertificateById(id),
        certificateApi.getAuditLog(id)
      ]);
      
      setCertificate(certData);
      setAuditLog(auditData);
      setError(null);
    } catch (err) {
      console.error('Failed to load certificate:', err);
      setError('Failed to load certificate details');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!confirm('Activate this certificate as the primary signing certificate?')) {
      return;
    }

    try {
      await certificateApi.activateCertificate(id);
      alert('Certificate activated successfully');
      loadCertificate();
    } catch (err) {
      alert('Failed to activate certificate: ' + err.message);
    }
  };

  const handleRevoke = async () => {
    const reason = prompt('Enter revocation reason:');
    if (!reason) return;

    try {
      await certificateApi.revokeCertificate(id, reason);
      alert('Certificate revoked successfully');
      loadCertificate();
    } catch (err) {
      alert('Failed to revoke certificate: ' + err.message);
    }
  };

  const handleCopyCertificate = async () => {
    if (certificate?.certificatePem) {
      const success = await copyToClipboard(certificate.certificatePem);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleDownloadCertificate = () => {
    if (certificate?.certificatePem) {
      downloadFile(
        certificate.certificatePem,
        `${certificate.alias}.pem`,
        'application/x-pem-file'
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/iso20022/certificates')} className="btn-secondary">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Certificates
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error || 'Certificate not found'}</span>
        </div>
      </div>
    );
  }

  // The key can leave only for an issued, unrevoked certificate, and only to an admin.
  const canExportKey = isAdmin()
    && certificate.status !== 'REVOKED'
    && Boolean(certificate.certificatePem);

  const getStatusBadgeClass = (status) => {
    const map = {
      'ACTIVE': 'badge-success',
      'PENDING': 'badge-warning',
      'REVOKED': 'badge-danger',
      'EXPIRED': 'badge-danger'
    };
    return map[status] || 'badge-info';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/certificates')}
            className="btn-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900 font-mono">
                {certificate.alias}
              </h1>
              {certificate.isPrimary && (
                <span className="badge bg-blue-100 text-blue-800 flex items-center space-x-1">
                  <CheckCircle className="w-3 h-3" />
                  <span>Primary</span>
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">Certificate ID: {certificate.id}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {certificate.status === 'PENDING' && (
            <button onClick={handleActivate} className="btn-primary">
              <CheckCircle className="w-4 h-4 mr-2" />
              Activate
            </button>
          )}
          {certificate.status === 'ACTIVE' && !certificate.isPrimary && (
            <>
              <button onClick={handleActivate} className="btn-secondary">
                Make Primary
              </button>
              <button onClick={handleRevoke} className="btn-danger">
                <XCircle className="w-4 h-4 mr-2" />
                Revoke
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-4 ${
        certificate.status === 'ACTIVE' ? 'bg-green-50 border border-green-200' :
        certificate.status === 'PENDING' ? 'bg-yellow-50 border border-yellow-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className={getStatusBadgeClass(certificate.status)}>
              {certificate.status}
            </span>
            <span className="text-sm text-gray-700">
              Created {formatRelativeTime(certificate.createdAt)} by {certificate.createdBy}
            </span>
          </div>
          {certificate.isExpiringSoon && !certificate.isExpired && (
            <div className="flex items-center space-x-2 text-warning-700">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">
                Expires in {certificate.daysUntilExpiry} days
              </span>
            </div>
          )}
          {certificate.isExpired && (
            <div className="flex items-center space-x-2 text-red-700">
              <XCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Certificate Expired</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['details', 'certificate', 'audit'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Certificate Information */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Certificate Information</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Subject</label>
                <p className="text-sm text-gray-900 mt-1 font-mono break-all">
                  {certificate.subject}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Issuer</label>
                <p className="text-sm text-gray-900 mt-1 font-mono break-all">
                  {certificate.issuer}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Serial Number</label>
                  <p className="text-sm text-gray-900 mt-1 font-mono">
                    {certificate.serialNumber}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {certificate.certificateType?.replace('_', ' ')}
                  </p>
                </div>
              </div>
              {certificate.caName && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Certificate Authority</label>
                  <p className="text-sm text-gray-900 mt-1">{certificate.caName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Cryptographic Details */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Cryptographic Details</h2>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Key Algorithm</label>
                  <p className="text-sm text-gray-900 mt-1">{certificate.keyAlgorithm}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Key Size</label>
                  <p className="text-sm text-gray-900 mt-1">{certificate.keySize} bits</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Signature Algorithm</label>
                <p className="text-sm text-gray-900 mt-1">{certificate.signatureAlgorithm}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Thumbprint (SHA-256)</label>
                <p className="text-sm text-gray-900 mt-1 font-mono break-all">
                  {certificate.thumbprintSha256}
                </p>
              </div>
            </div>
          </div>

          {/* Validity Period */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Validity Period</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Valid From</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatDate(certificate.notBefore, 'PPpp')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Valid Until</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatDate(certificate.notAfter, 'PPpp')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Days Remaining</label>
                <p className={`text-sm mt-1 font-medium ${
                  certificate.isExpired ? 'text-red-600' :
                  certificate.isExpiringSoon ? 'text-warning-600' :
                  'text-green-600'
                }`}>
                  {certificate.isExpired ? 'Expired' : `${certificate.daysUntilExpiry} days`}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Usage Statistics</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Total Signatures</label>
                <p className="text-2xl font-bold text-primary-600 mt-1">
                  {certificate.usageCount?.toLocaleString() || 0}
                </p>
              </div>
              {certificate.lastUsedAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Used</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(certificate.lastUsedAt, 'PPpp')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatRelativeTime(certificate.lastUsedAt)}
                  </p>
                </div>
              )}
              {certificate.activatedAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Activated</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(certificate.activatedAt, 'PPpp')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Revocation Info */}
          {certificate.status === 'REVOKED' && (
            <div className="card lg:col-span-2">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Revocation Information</h2>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Revoked At</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {formatDate(certificate.revokedAt, 'PPpp')}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Revoked By</label>
                    <p className="text-sm text-gray-900 mt-1">{certificate.revokedBy}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Reason</label>
                  <p className="text-sm text-gray-900 mt-1">{certificate.revocationReason}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'certificate' && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Certificate (PEM Format)</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCopyCertificate}
                className="btn-secondary text-sm flex items-center space-x-2"
              >
                <Copy className="w-4 h-4" />
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button
                onClick={handleDownloadCertificate}
                className="btn-secondary text-sm flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
              {canExportKey && (
                <button
                  onClick={() => setShowExport(true)}
                  className="btn-secondary text-sm flex items-center space-x-2"
                >
                  <KeyRound className="w-4 h-4" />
                  <span>Export key (.p12)</span>
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            <pre className="bg-gray-900 text-green-400 p-4 rounded font-mono text-xs overflow-x-auto">
              {certificate.certificatePem}
            </pre>
          </div>
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Audit Log</h2>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {auditLog.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No audit log entries</p>
              ) : (
                auditLog.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="flex items-start space-x-4 pb-4 border-b last:border-0"
                  >
                    <div className="flex-shrink-0">
                      <Activity className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {entry.action.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{entry.details}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>{entry.performedBy}</span>
                        <span>•</span>
                        <span>{formatDate(entry.performedAt, 'PPpp')}</span>
                        {entry.ipAddress && (
                          <>
                            <span>•</span>
                            <span className="font-mono">{entry.ipAddress}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showExport && (
        <ExportKeystoreModal
          certificate={certificate}
          onClose={() => setShowExport(false)}
          onExported={() => certificateApi.getAuditLog(id).then(setAuditLog).catch(() => {})}
        />
      )}
    </div>
  );
};

export default CertificateDetail;
